import { map } from 'lodash';
import Channel from '@/models/Channel';
import Learner from '@/models/Learner';
import Teacher from '@/models/Teacher';
import LearningUnit from '@/models/LearningUnit';
import Section from '@/models/Section';
import SendbirdUser from '@/models/SendbirdUser';
import User from '@/models/User';
import UserRole from '@/models/UserRole';
import LearningUnitMember from '@/models/LearningUnitMember';

async function loadMetadataAndRelatedData(groupChannels) {
  let channels = [];

  for (let index = 0; index < groupChannels.length; index++) {
    const channel = groupChannels[index];

    const metaData = await channel.getMetaData(['learning_unit', 'section', 'learner', 'teachers']);

    Channel.insertOrUpdate({ data: { ...channel, metadata: metaData } });

    channels.push({ ...channel, metadata: metaData });
  }

  if (channels && channels.length > 0) {
    let learningUnitIds = channels.reduce((res, channel) => {
      res.push(channel.metadata.learning_unit);

      return res;
    }, []);

    let teacherIds = channels.reduce((res, channel) => {
      const { teachers = '' } = channel.metadata;

      if (teachers.length) {
        res = [...res, ...teachers.split(',')];
      }

      return res;
    }, []);

    let learnerIds = channels.reduce((res, channel) => {
      res.push(channel.metadata.learner);

      return res;
    }, []);

    let sectionIds = channels.reduce((res, channel) => {
      res.push(channel.metadata.section);

      return res;
    }, []);

    learningUnitIds = [...new Set(learningUnitIds)];
    teacherIds = [...new Set(teacherIds)];
    learnerIds = [...new Set(learnerIds)];
    sectionIds = [...new Set(sectionIds)];

    if (learningUnitIds.length) {
      await LearningUnit.api().fetch({
        id_in: learningUnitIds
      });
    }

    if (teacherIds.length) {
      await Teacher.api().fetch({
        id_in: teacherIds
      });
    }

    if (learnerIds.length) {
      await Learner.api().fetch({
        id_in: learnerIds
      });
    }

    if (sectionIds.length) {
      await Section.api().fetch({
        id_in: sectionIds
      });
    }
  }

  return channels;
}

export const channelMixin = {
  data() {
    return {
      channelMixin_channelListQuery: null
    };
  },

  computed: {
    $_channelMixin_isAuthConnected() {
      const auth = User.query().first();

      return auth ? SendbirdUser.isConnected(auth.id) : false;
    },

    $_channelMixin_hasMoreChannels() {
      return this.channelMixin_channelListQuery
        ? this.channelMixin_channelListQuery.hasNext
        : false;
    }
  },

  methods: {
    async $_channelMixin_connect(user) {
      console.log('Connect Sendbird: ', user);

      if (!SendbirdUser.isConnected(user.id)) {
        await SendbirdUser.api().userConnect(user);

        SendbirdUser.getTotalUnreadMessageCount(user.id);

        Channel.registerEventHandlers(user.id);
      }
    },

    async $_channelMixin_disconnect() {
      console.log('Disconnect Sendbird');

      SendbirdUser.api().userDisconnect();

      Channel.deleteAll();
    },

    async $_channelMixin_loadLearningUnitChannels(
      learningUnitId,
      { includeEmpty = false, limit = 20 } = {}
    ) {
      if (!this.channelMixin_channelListQuery) {
        this.channelMixin_channelListQuery = await Channel.api().getChannelListBykey(
          'learning_unit',
          [`${learningUnitId}`],
          includeEmpty,
          limit
        );
      }

      let groupChannels = await this.channelMixin_channelListQuery.next();

      return await loadMetadataAndRelatedData(groupChannels);
    },

    async $_channelMixin_loadUserChannels(userId, { limit = 20 } = {}) {
      if (!this.channelMixin_channelListQuery) {
        this.channelMixin_channelListQuery = await Channel.api().getChannelListBykey(
          `${userId}`,
          ['teacher', 'learner'],
          false,
          limit
        );
      }

      let groupChannels = await this.channelMixin_channelListQuery.next();

      return await loadMetadataAndRelatedData(groupChannels);
    },

    async $_channelMixin_loadSectionChannels(learningUnitId, sectionId, { limit = 20 } = {}) {
      if (!this.channelMixin_channelListQuery) {
        this.channelMixin_channelListQuery = await Channel.api().getSectionDirectMessageChannels(
          learningUnitId,
          sectionId,
          limit
        );
      }

      let groupChannels = await this.channelMixin_channelListQuery.next();

      return await loadMetadataAndRelatedData(groupChannels);
    },

    async $_channelMixin_loadDirectMessageChannel(learningUnitId, sectionId, learnerId) {
      const groupChannel = await Channel.api().getChannel(learningUnitId, learnerId, sectionId);

      return await loadMetadataAndRelatedData([groupChannel]);
    },

    async $_channelMixin_createDirectMessageChannel(learningUnitId, sectionId, learnerId) {
      await LearningUnit.api().fetchByLearningUnitId(learningUnitId);

      const learningUnitMembers = LearningUnitMember.query()
        .where('learning_unit_id', learningUnitId)
        .get();

      const ownerRole = UserRole.learningUnitOwner();
      const instructorRole = UserRole.learningUnitInstructor();

      const teachers = learningUnitMembers.filter(
        (learning_unit_member) =>
          learning_unit_member.user_role === ownerRole.id ||
          learning_unit_member.user_role === instructorRole.id
      );

      console.log(learningUnitId, sectionId, map(teachers, 'user.id'), learnerId);

      return Channel.api().createChannelWithMetaData(
        learningUnitId,
        sectionId,
        map(teachers, 'user.id'),
        learnerId
      );
    },

    async $_channelMixin_addUserToLearningUnitChannels(learningUnitId, userId) {
      const { $_channelMixin_isAuthConnected: isConnected } = this;

      if (!isConnected) return;

      let promises,
        channelUrls = [];

      if (learningUnitId) {
        let hasMoreChannel = true;

        while (hasMoreChannel) {
          let channels = await this.$_channelMixin_loadLearningUnitChannels(learningUnitId, {
            includeEmpty: true,
            limit: 100
          });

          if (channels && channels.length > 0) {
            channelUrls = [...channelUrls, ...map(channels, 'url')];
          }

          hasMoreChannel = this.$_channelMixin_hasMoreChannels;
        }

        this.channelMixin_channelListQuery = null;
      }

      if (channelUrls.length) {
        promises = await Promise.all(
          channelUrls.map((channelUrl) => Channel.api().inviteInstructor(channelUrl, [userId]))
        );
      }

      return promises;
    },

    async $_channelMixin_removeUserFromLearningUnitChannels(learningUnitId, userId) {
      const { $_channelMixin_isAuthConnected: isConnected } = this;

      if (!isConnected) return;

      let promises,
        channelUrls = [];

      if (learningUnitId) {
        let hasMoreChannel = true;

        while (hasMoreChannel) {
          let channels = await this.$_channelMixin_loadLearningUnitChannels(learningUnitId, {
            includeEmpty: true,
            limit: 100
          });

          if (channels && channels.length > 0) {
            channelUrls = [...channelUrls, ...map(channels, 'url')];
          }

          hasMoreChannel = this.$_channelMixin_hasMoreChannels;
        }

        this.channelMixin_channelListQuery = null;
      }

      if (channelUrls.length) {
        promises = await Promise.all(
          channelUrls.map((channelUrl) => Channel.api().deleteInstructor(channelUrl, userId))
        );
      }

      return promises;
    }
  }
};
